import {
  BILLING_STATUS,
  PROCESS_STATUS,
  SCHEDULED_TYPE,
  TypeProductDecoratedScheduledItem,
  TypeScheduledItemRecord,
  TypeSubscriptionRecord,
} from '@/lib/api/subscriptionManager/types';
import _flatMap from 'lodash/flatMap';
import { TypeProduct } from '@/lib/api/productCatalog/types';

type GetScheduledItemsFilters = {
  filterPreviousPlays?: boolean;
  filterProcessedItems?: boolean;
};

export function getScheduledItems(
  subscription?: TypeSubscriptionRecord,
  customFilters?: GetScheduledItemsFilters,
) {
  if (!subscription || !subscription.schedule_sections) {
    console.warn(
      `[getScheduledItems]: unable to access scheduled items on provided subscription: ${subscription}`,
    );
    return [];
  }

  const defaultFilters = {
    filterPreviousPlays: true,
    filterProcessedItems: false,
  };

  const opts: GetScheduledItemsFilters = {
    ...defaultFilters,
    ...customFilters,
  };

  let items = _flatMap(
    subscription.schedule_sections,
    (s) => s.scheduled_items,
  );

  if (opts.filterPreviousPlays) {
    items = items.filter(_filterPreviousPlays);
  }

  if (opts.filterProcessedItems) {
    items = items.filter(_filterProcessedItems);
  }

  return items;
}

export function decorateScheduledItemWithProductData(
  scheduledItem?: TypeScheduledItemRecord,
  playkitProducts?: TypeProduct[],
) {
  if (!playkitProducts) {
    console.warn(
      '[decorateScheduledItemWithProductData] no product data provided, unable to decorate.',
    );
    return {
      ...scheduledItem,
      product: undefined,
    } as TypeProductDecoratedScheduledItem;
  }

  const targetProduct = playkitProducts.find(
    (product) => product.uuid === scheduledItem?.product_uuid,
  );

  return {
    ...scheduledItem,
    product: targetProduct,
  } as TypeProductDecoratedScheduledItem;
}

export function isItemSkipRequested(scheduledItem?: TypeScheduledItemRecord) {
  return scheduledItem?.process_status === PROCESS_STATUS.SKIP_REQUESTED;
}

export function isItemSkippable(scheduledItem?: TypeScheduledItemRecord) {
  return scheduledItem?.process_status === PROCESS_STATUS.AWAITING_PROCESS;
}

export function isItemPrepaid(scheduledItem?: TypeScheduledItemRecord) {
  return scheduledItem?.billing_status === BILLING_STATUS.PREPAY;
}

export function isItemOnlySkippableWithCXIntervention(
  scheduledItem?: TypeScheduledItemRecord,
) {
  return isItemPrepaid(scheduledItem);
}

function _filterProcessedItems(scheduledItem?: TypeScheduledItemRecord) {
  if (!scheduledItem) return false;

  const unprocessedStatuses = [
    PROCESS_STATUS.AWAITING_PROCESS,
    PROCESS_STATUS.SKIP_REQUESTED,
  ];

  return unprocessedStatuses.includes(scheduledItem.process_status);
}

function _filterPreviousPlays(scheduledItem?: TypeScheduledItemRecord) {
  return scheduledItem?.scheduled_type !== SCHEDULED_TYPE.PREVIOUS_PLAY;
}
